import React, { useEffect, useState, useMemo } from "react";
import Loader from "../Loader/Loader";
import useApiLoader from "../../Utils/useApiLoader";
import { getUser, hasPermission, isAdmin } from "../../Utils/auth";
import { Link } from "react-router-dom";
import PlusIcon from "../../assets/icons/plus";
import StatCard from "../Common/StatCard";
import NoteIcon from "../../assets/icons/note";
import LogIcon from "../../assets/icons/log";
import EmployeePayrollTable from "../Common/EmployeePayrollTable";
import DollarIcon from "../../assets/icons/dollar";
import api from "../../setupMiddleware";
import { ToastContainer, toast, Bounce } from "react-toastify";
import moment from "moment/moment";
import { FaFilter } from "react-icons/fa";
import Popup from "../Common/Popup";
import CustomSelect from "../Common/CustomSelect";
import { formatCurrency } from "../../Utils/getUserUtitls";
import { exportSalaryReport } from "../../Utils/exportSalaryReport";
import { PiFileXlsFill } from "react-icons/pi";
import { TbCurrencyDirham, TbCurrencyRupee } from "react-icons/tb";

const Payroll = () => {
  const loading = useApiLoader();

  const [empData, setEmpData] = useState([]);
  const prevMonthIndex = moment().month() - 1;
  const defaultMonthValue = prevMonthIndex >= 0 ? prevMonthIndex + 1 : 12;
  const [monthFilter, setMonthFilter] = useState(defaultMonthValue);
  const [yearFilter, setYearFilter] = useState(moment().year());
  const [selectedMonth, setSelectedMonth] = useState(defaultMonthValue);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [openfilter, setOpenFilter] = useState(false);
  const [salarySummay, setSalarySummary] = useState(null);
  const [reportFile, setReportFile] = useState(null);

  const stats = [
    {
      title: "Total Salary Dubai",
      value:
        `AED ${formatCurrency(salarySummay?.location_1?.net_salary_sum)}` || 0,
      Icon: TbCurrencyDirham,
      iconSize: 20,
    },
    {
      title: "Total Employee Dubai",
      value: salarySummay?.location_1?.employee_count || 0,
      Icon: LogIcon,
    },
    {
      title: "Total Salary India",
      value:
        `₹ ${formatCurrency(salarySummay?.location_2?.net_salary_sum)}` || 0,
      Icon: TbCurrencyRupee,
      iconSize: 20,
    },
    {
      title: "Total Employee India",
      value: salarySummay?.location_2?.employee_count || 0,
      Icon: NoteIcon,
    },
  ];

  const currentUser = getUser();

  // ✅ Allowed years (2020 to current year)
  const currentYear = moment().year();
  const yearOptions = Array.from({ length: currentYear - 2020 + 1 }, (_, i) => {
    const year = 2020 + i;
    return { label: year.toString(), value: year };
  });

  // ✅ Months: show all months if past year, only till current month if current year
  const monthOptions = useMemo(() => {
    const months = moment.monthsShort(); // ["Jan", "Feb", ... "Dec"]

    if (yearFilter === currentYear) {
      const currentMonthIndex = moment().month(); // 0-based current month index
      // slice up to currentMonthIndex (excludes current month)
      return months.slice(0, currentMonthIndex).map((m, index) => ({
        label: m,
        value: index + 1, // month number (1-12)
      }));
    }

    return months.map((m, index) => ({
      label: m,
      value: index + 1, // month number (1-12)
    }));
  }, [yearFilter]);

  const getAllSalaries = async () => {
    api
      .post("/api/salaries", {
        month: monthFilter,
        year: yearFilter,
      })
      .then((res) => {
        if (res && res.status === 200) {
          setEmpData(res.data?.salaries);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const getSalarySummary = async () => {
    api
      .post("/api/salaries/payroll-summary", {
        month: monthFilter,
        year: yearFilter,
      })
      .then((res) => {
        if (res && res.status === 200) {
          setSalarySummary(res.data);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const getReportFile = async () => {
    await api
      .post("/api/salaries/csv-file", {
        month: monthFilter,
        year: yearFilter,
      })
      .then((res) => {
        if (res && res.status === 200) {
          setReportFile(res?.data?.csv_files?.[0]?.filepath);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const generateAllSalaries = async () => {
    api
      .post("/api/salaries/generate-monthly", {
        month: monthFilter,
        year: yearFilter,
      })
      .then((res) => {
        if (res && res.status === 200) {
          toast.success(res.data?.message, {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
          });
          getAllSalaries();
        }
      })
      .catch((error) => {
        toast.error(error.response.data?.error, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      });
  };

  const generateAllPayslip = async () => {
    api
      .post("/api/create-payslip", {
        month: monthFilter,
        year: yearFilter,
      })
      .then((res) => {
        if (res && res.status === 200) {
          toast.success(res.data?.message, {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
          });
          getAllSalaries();
        }
      })
      .catch((error) => {
        toast.error(error.response.data?.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      });
  };

  const getCSVData = async () => {
    try {
      const res = await api.post("/api/salaries/csv-data", {
        month: monthFilter,
        year: yearFilter,
      });

      if (res.status !== 200) throw new Error("Failed to fetch CSV data");

      const file = await exportSalaryReport(res.data, monthFilter, yearFilter);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("month", monthFilter);
      formData.append("year", yearFilter);
      formData.append("created_by", currentUser?.id);

      const uploadRes = await api.post("/api/salaries/upload-csv", formData);

      if (uploadRes.status === 201) {
        const fileRes = await api.post("/api/salaries/csv-file", {
          month: monthFilter,
          year: yearFilter,
        });

        if (fileRes.status === 200) {
          toast.success("File Generated Successfully!", {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          });
          setReportFile(fileRes?.data?.csv_files?.[0]?.filepath);
          window.open(fileRes?.data?.csv_files?.[0]?.filepath, "_blank");
        }
      } else {
        toast.error(uploadRes.data?.error || "Upload failed", {
          position: "top-left",
          autoClose: 5000,
          theme: "colored",
          transition: Bounce,
        });
      }
    } catch (error) {
      console.error("❌ Upload Error:", error);

      // Safely handle different error cases
      const msg =
        error.response?.data?.error ||
        error.message ||
        "Something went wrong during upload.";

      toast.error(msg, {
        position: "top-left",
        autoClose: 5000,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const updateFilter = () => {
    setSelectedMonth(monthFilter);
    setSelectedYear(yearFilter);
    getAllSalaries();
    setOpenFilter(false);
    getSalarySummary();
  };

  useEffect(() => {
    getAllSalaries();
    getSalarySummary();
    getReportFile();
  }, []);

  const lastMonthMoment = moment().subtract(1, "month");
  const lastMonth = lastMonthMoment.month() + 1; // 1-based month number
  const lastMonthYear = lastMonthMoment.year(); // year of last month

  // check
  const isLastMonthAndYear =
    selectedMonth === lastMonth && selectedYear === lastMonthYear;

  return (
    <>
      {loading && <Loader opacity={90} fullscreen />}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full mx-auto px-4 py-3 gap-4">
        {/* Left Content */}
        <p>
          <span className="block text-xl sm:text-2xl font-medium text-[#de2b26]">
            Payroll
          </span>
          <span className="block text-sm text-[#142b70]/50">
            Manage employee salaries and payroll processing
          </span>
        </p>
        <div className="flex gap-2">
          {(isAdmin() || hasPermission("generate_salary")) &&
            isLastMonthAndYear && (
              <>
                {empData.length === 0 ? (
                  <button
                    onClick={generateAllSalaries}
                    disabled={!empData.length === 0}
                    className="flex items-center gap-2 px-4 py-2 rounded bg-[#de2b26] hover:bg-[#c30f0a] transition text-white text-sm font-medium"
                  >
                    <PlusIcon iconClass="w-4 h-4" color="#ffffff" />
                    Generate Salary
                  </button>
                ) : (
                  <button
                    onClick={generateAllPayslip}
                    disabled={!empData.length === 0}
                    className="flex items-center gap-2 px-4 py-2 rounded bg-[#de2b26] hover:bg-[#c30f0a] transition text-white text-sm font-medium"
                  >
                    <PlusIcon iconClass="w-4 h-4" color="#ffffff" />
                    Generate Payslip
                  </button>
                )}
              </>
            )}
          {empData.length > 0 && !reportFile && (
            <button
              onClick={getCSVData}
              className="flex items-center gap-2 px-4 py-2 rounded bg-[#de2b26] hover:bg-[#c30f0a] transition text-white text-sm font-medium"
            >
              <PiFileXlsFill className={`text-xs text-[#fff]`} size={15} />
              Generate Report
            </button>
          )}{" "}
          {empData.length > 0 && reportFile && (
            <Link
              to={reportFile}
              target="_blank"
              className="flex items-center gap-2 px-4 py-2 rounded bg-[#de2b26] hover:bg-[#c30f0a] transition text-white text-sm font-medium"
            >
              <PiFileXlsFill className={`text-xs text-[#fff]`} size={15} />
              Download Report
            </Link>
          )}
          <button
            onClick={() => setOpenFilter(true)}
            className="flex items-center gap-2 px-4 py-2 rounded bg-[#de2b26] hover:bg-[#c30f0a] transition text-white text-sm font-medium"
          >
            <FaFilter size={15} color="#ffffff" />
          </button>
        </div>
        <Popup
          isOpen={openfilter}
          onClose={() => setOpenFilter(false)}
          title="Change Month & Year"
        >
          <div className="flex flex-grow-0 flex-shrink-0 gap-4"></div>
          <div className="flex justify-between gap-5">
            <div className="flex flex-col gap-2 w-full">
              <p className="text-sm text-[#142b70]/80">Month</p>
              <CustomSelect
                options={monthOptions}
                placeholder="Month"
                value={monthFilter}
                onChange={(opt) => setMonthFilter(opt.value)}
                width="w-full"
                height="h-[46px]"
                fontSize="14px"
                optionFontSize="sm"
              />
            </div>
            <div className="flex flex-col gap-2 w-full">
              <p className="text-sm text-[#142b70]/80">Year</p>
              <CustomSelect
                options={yearOptions}
                placeholder="Year"
                value={yearFilter}
                onChange={(opt) => setYearFilter(opt.value)}
                width="w-full"
                height="h-[46px]"
                fontSize="14px"
                optionFontSize="sm"
              />
            </div>
          </div>
          {/* Footer */}
          <div className="flex justify-end gap-4">
            <button
              onClick={() => setOpenFilter(false)}
              className="px-10 py-3.5 rounded-md border border-[#de2b26] text-[#de2b26] font-medium"
            >
              Close
            </button>
            <button
              onClick={updateFilter}
              className="px-10 py-3.5 rounded-md border border-[#de2b26] bg-[#de2b26] text-white font-medium"
            >
              Apply
            </button>
          </div>
        </Popup>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full mx-auto px-4 py-3">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>
      <div className="w-full mx-auto px-4 py-3 relative">
        <div className="w-full mx-auto px-4 py-3 relative rounded-xl bg-white border border-[#a48572]/10">
          <div className="flex flex-col justify-start items-start gap-[20px]">
            <div className="flex flex-col justify-start items-start flex-grow-0 flex-shrink-0 relative gap-5">
              <p className="flex-grow-0 flex-shrink-0 text-base font-medium text-left text-[#142b70]">
                Salaries for{" "}
                <b>
                  {moment()
                    .month(selectedMonth - 1)
                    .format("MMM")}{" "}
                  {selectedYear}
                </b>
              </p>
            </div>
            <EmployeePayrollTable
              data={empData}
              // designationOptions={designationOptions}
              // handleDisableEnable={handleStatusToggle}
            />
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default Payroll;
